import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const COOLDOWN_SECONDS = 60;                                   // matches Supabase's default OTP window
const COOLDOWN_STORAGE_KEY = 'rc:auth:cooldown_until';         // persisted so a page refresh doesn't reset it

const AuthModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('email'); // 'email' | 'sent'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { sendMagicLink, verifyOtpCode } = useAuth();

  // Restore any in-flight cooldown between renders / page refreshes.
  useEffect(() => {
    const until = parseInt(localStorage.getItem(COOLDOWN_STORAGE_KEY) || '0', 10);
    const remaining = Math.max(0, Math.ceil((until - Date.now()) / 1000));
    if (remaining > 0) setCooldown(remaining);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => {
      setCooldown(prev => {
        const next = prev - 1;
        if (next <= 0) localStorage.removeItem(COOLDOWN_STORAGE_KEY);
        return Math.max(0, next);
      });
    }, 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const startCooldown = () => {
    const until = Date.now() + COOLDOWN_SECONDS * 1000;
    localStorage.setItem(COOLDOWN_STORAGE_KEY, String(until));
    setCooldown(COOLDOWN_SECONDS);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email.trim() || cooldown > 0) return;
    setIsLoading(true);
    try {
      await sendMagicLink(email.trim());
      setStep('sent');
      startCooldown();
    } catch (error) {
      // If Supabase rate-limited us anyway, still start the cooldown to stop the spam.
      if (error?.status === 429) startCooldown();
      toast.error(error.message || 'Failed to send sign-in link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const clean = code.replace(/\D/g, '').trim();
    // Accept any 6-10 digit code — Supabase OTP length is configurable per project.
    if (clean.length < 6 || clean.length > 10 || isVerifying) return;
    setIsVerifying(true);
    try {
      await verifyOtpCode(email.trim(), clean);
      // onAuthStateChange in AuthContext fires on success → user signed in → close modal
      handleClose();
    } catch (error) {
      toast.error(error.message || "Couldn't verify that code");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    setStep('email');
    setEmail('');
    setCode('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 sm:p-4"
        style={{ backgroundColor: 'rgba(44, 36, 23, 0.5)' }}
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.98 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md rounded-2xl shadow-2xl p-6 sm:p-8 relative"
          style={{ backgroundColor: '#FFF8F0' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="auth-modal"
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-charcoal/5 transition-colors"
            data-testid="close-auth-modal"
          >
            <X size={20} className="text-charcoal/60" />
          </button>

          {step === 'email' ? (
            <>
              <h2 className="font-serif text-3xl text-charcoal mb-2 text-center">
                Sign in
              </h2>
              <p className="text-charcoal/60 text-center mb-8 text-sm">
                We'll email you a magic link — no password needed.
              </p>
              <form onSubmit={handleSend} className="space-y-4">
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoFocus
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50 transition-colors"
                    style={{ fontSize: '16px' }}
                    data-testid="email-input"
                  />
                </div>
                <motion.button
                  type="submit"
                  disabled={isLoading || !email.trim() || cooldown > 0}
                  whileHover={{ scale: cooldown > 0 ? 1 : 1.02 }}
                  whileTap={{ scale: cooldown > 0 ? 1 : 0.98 }}
                  className="w-full py-3 rounded-full font-medium text-white transition-[box-shadow,transform,background-color,opacity] duration-300 disabled:opacity-50"
                  style={{ background: '#ff780a', boxShadow: '0 4px 20px rgba(255, 120, 10, 0.3)' }}
                  data-testid="send-magic-link-btn"
                >
                  {cooldown > 0
                    ? `Try again in ${cooldown}s`
                    : isLoading ? 'Sending...' : 'Send magic link'}
                </motion.button>
                {cooldown > 0 && (
                  <p className="text-xs text-charcoal/50 text-center">
                    Magic links expire when a new one is requested. Wait a moment to avoid rate limits.
                  </p>
                )}
              </form>
            </>
          ) : (
            <div className="text-center py-2">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                className="flex justify-center mb-5"
              >
                <CheckCircle size={48} className="text-orange-primary" />
              </motion.div>
              <h2 className="font-serif text-2xl text-charcoal mb-2">
                Check your inbox
              </h2>
              <p className="text-charcoal/60 text-sm">
                We sent a sign-in email to
              </p>
              <p className="font-medium text-charcoal mb-5">{email}</p>

              <form onSubmit={handleVerifyCode} className="space-y-3">
                <label className="block text-left text-xs uppercase tracking-wider text-charcoal/50">
                  Enter the code from the email
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6,10}"
                  maxLength={10}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Paste or type"
                  autoFocus
                  data-testid="otp-code-input"
                  className="w-full text-center font-mono tracking-[0.3em] text-2xl px-4 py-3 rounded-xl border border-charcoal/10 bg-white/70 focus:outline-none focus:border-orange-primary/50 transition-colors"
                  style={{ letterSpacing: '0.3em' }}
                />
                <motion.button
                  type="submit"
                  disabled={code.length < 6 || code.length > 10 || isVerifying}
                  whileHover={{ scale: (code.length >= 6 && code.length <= 10) ? 1.02 : 1 }}
                  whileTap={{ scale: (code.length >= 6 && code.length <= 10) ? 0.98 : 1 }}
                  className="w-full py-3 rounded-full font-medium text-white transition-[box-shadow,transform,background-color,opacity] duration-300 disabled:opacity-50"
                  style={{ background: '#ff780a', boxShadow: '0 4px 20px rgba(255, 120, 10, 0.3)' }}
                  data-testid="verify-code-btn"
                >
                  {isVerifying ? 'Verifying…' : 'Sign in'}
                </motion.button>
              </form>

              <p className="text-charcoal/40 text-xs mt-4">
                Or click the link in the email. If the link doesn't work (some email clients break it), the code above always will.
              </p>
              <button
                onClick={() => { setStep('email'); setEmail(''); setCode(''); }}
                className="mt-4 text-sm text-charcoal/50 hover:text-charcoal underline transition-colors"
                data-testid="try-different-email-btn"
              >
                Use a different email
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthModal;
